import { Box, Button, Divider, Grid2, IconButton, Input, Paper, SpeedDial, SpeedDialAction, SpeedDialIcon, Table, TableBody, TableCell, tableCellClasses, TableFooter, TableHead, TableRow, TextField, Typography, useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";
import _ from "lodash";
import { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import InlineEditableField from '../common/inline.editable.field';
import { getRangeLabel } from "../common/util";
import { DialogsContext, SessionDataContext } from "../contexts";
import ScoreAvatar from "../enrollment/score.avatar";
import StudentAvatar from '../student/student.avatar';
import { studentApi } from '../student/student.queries';
import SubjectName from '../subject/subject.name';
import { termApi } from '../term/term.queries';
import { topicApi } from '../topic/topic.queries';
import { ratingApi } from './rating.queries';
import { reportCardApi } from './reportCard.queries';
import DeleteIcon from '@mui/icons-material/Delete';

export default function ReportCardView(props) {
	const navigate = useNavigate();
	const { termId, studentId } = useParams();
	const useGetTerm = termApi.useGet(termId);
	const term = useGetTerm?.data;

	const useGetStudent = studentApi.useGet(studentId);
	const student = useGetStudent?.data;

	const useGetReportCard = reportCardApi.useLookup(studentId, termId);
	const useSaveReportCard = reportCardApi.useSave();
	const reportCard = useGetReportCard?.data;

	const useSaveRating = ratingApi.useSave();
	const useSaveTopic = topicApi.useSave();
	const useDeleteTopic = topicApi.useDelete();

	const fields = ['K/U', 'Thinking', 'Communication', 'Application'];

	const theme = useTheme();
	const { dialogs } = useContext(DialogsContext);
	const sessionData = useContext(SessionDataContext);

	if (!term || !student || !reportCard) {
		return;
	}

	const addTopic = async (e, subject) => {
		const input = e.currentTarget.closest('tr').querySelector('input');
		const name = input.value;
		if (!name) {
			return;
		}
		await useSaveTopic.mutateAsync({ schoolId: term.schoolId, termId: term.id, subjectId: subject.id, name: name });
		input.value = '';
	};

	const renderSubject = (subject, enrollments) => {
		enrollments = _.sortBy(enrollments, 'course.name');
		const termLength = term.endDate - term.startDate;
		const topics = term.topics.filter(topic => topic.subjectId == subject.id);

		return (
			<Box key={subject.id} sx={{ mb: 4 }}>
				<SubjectName subject={subject} size={24} variant="h5" sx={{ mb: 2 }} />
				<Grid2 container spacing={4}>
					<Grid2 size={{ xs: 12, md: 4, lg: 3, xl: 2, xxl: 1 }}>
						<Stack direction="column">
							<Typography variant="subtitle2">Courses</Typography>
							{enrollments.map(enrollment => (
								<Stack key={enrollment.id}>
									<ScoreAvatar score={enrollment.score} size={24} force={true} />
									<Typography>{enrollment.course.name}</Typography>
								</Stack>
							))}
						</Stack>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 8, lg: 9, xl: 10, xxl: 11 }}>
						<Table sx={{ [`& .${tableCellClasses.root}`]: { border: '1px solid rgba(224, 224, 224, 1)', }, }}>
							<TableHead sx={{ height: 40 }}>
								<TableRow>
									<TableCell>Topic</TableCell>
									{fields.map(field => (
										<TableCell key={field}>{field}</TableCell>
									))}
									<TableCell></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{_.orderBy(topics, 'name').map(topic => (
									<TableRow key={topic.id} className="hover-toggle">
										<TableCell>
											<InlineEditableField model={topic} field="name" mutator={useSaveTopic} slots={{ input: { variant: 'standard' } }} component={TextField} />
										</TableCell>
										{fields.map(field => {
											const rating = _.find(topic.ratings, { field }) ?? { rateableType: 'topic', rateableId: topic.id, field: field };
											return (
												<TableCell key={field}>
													<InlineEditableField model={rating} field="value" mutator={useSaveRating} slots={{ input: { variant: 'standard' } }} component={TextField} />
												</TableCell>
											);
										})}
										<TableCell>
											<IconButton className="hover" onClick={() => useDeleteTopic.mutateAsync(topic.id)} >
												<DeleteIcon />
											</IconButton>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
							<TableFooter sx={{ height: 40 }}>
								<TableRow>
									<TableCell>
										<TextField variant="standard" placeholder="New Topic" onKeyDown={(e) => e.key === 'Enter' ? addTopic(e, subject) : ''} />
									</TableCell>
									<TableCell colSpan={fields.length + 1}>
										<Button variant="outlined" onClick={(e) => addTopic(e, subject)}>Add</Button>
									</TableCell>
								</TableRow>
							</TableFooter>
						</Table>
					</Grid2>
				</Grid2>
			</Box>
		);
	};

	const groups = _.chain(term.enrollments).filter({ studentId }).sortBy(['course.subject.name', 'course.name']).groupBy('course.subjectId').map((enrollments, subjectId) => {
		const subject = enrollments[0].course.subject;
		return renderSubject(subject, enrollments);
	}).value();

	return (
		<Box sx={{ p: 4 }}>
			<Stack sx={{ mb: 4 }} justifyContent="center" alignItems="center">
				<Typography variant="h4">Report Card</Typography>
			</Stack>

			<Paper elevation={4} sx={{ m: 4, p: 4 }}>
				<Stack sx={{ mb: 4 }} justifyContent="space-between" alignItems="center">
					<Stack alignItems="center" spacing={4}>
						<StudentAvatar student={student} size={128} variant="rounded" />
						<Stack direction="column" spacing={1}>
							<Typography variant="h3" sx={{ lineHeight: 1 }}>{student.name}</Typography>
							<Typography variant="h6">{term.name}</Typography>
							<Typography variant="h6">{getRangeLabel(term.startDate, term.endDate)}</Typography>
							<Typography variant="h6">{term.grade}</Typography>
						</Stack>
					</Stack>
				</Stack>

				<Divider sx={{ mb: 4 }} />

				<Grid2 container spacing={4}>
					<Grid2 size={{ xs: 12 }}>
						<Typography>Overall Feedback</Typography>
						<InlineEditableField model={reportCard} field="overall" mutator={useSaveReportCard} slots={{ input: { variant: 'standard', multiline: true, minRows: 4 } }} component={TextField} />
					</Grid2>

					<Grid2 size={{ xs: 12, md: 4 }}>
						<Typography>Strengths</Typography>
						<InlineEditableField model={reportCard} field="strengths" mutator={useSaveReportCard} slots={{ input: { variant: 'standard', multiline: true, minRows: 4 } }} component={TextField} />
					</Grid2>
					<Grid2 size={{ xs: 12, md: 4 }}>
						<Typography>Areas of Improvement</Typography>
						<InlineEditableField model={reportCard} field="weaknesses" mutator={useSaveReportCard} slots={{ input: { variant: 'standard', multiline: true, minRows: 4 } }} component={TextField} />
					</Grid2>
					<Grid2 size={{ xs: 12, md: 4 }}>
						<Typography>Goals</Typography>
						<InlineEditableField model={reportCard} field="goals" mutator={useSaveReportCard} slots={{ input: { variant: 'standard', multiline: true, minRows: 4 } }} component={TextField} />
					</Grid2>
				</Grid2>

				<Divider sx={{ my: 4 }} />

				{groups}

				<SpeedDial sx={{ position: 'fixed', bottom: 10, right: (sessionData.drawerOpen ? 350 : 40), alignItems: 'end', '.MuiSpeedDial-actions': { alignItems: 'end', '.MuiFab-circular': { borderRadius: 2, width: 'auto' } } }} color="primary" icon={<SpeedDialIcon />} ariaLabel="">
					<SpeedDialAction icon={<Typography sx={{ p: 2 }}>Enroll a Student</Typography>} onClick={() => dialogs.open('enrollment', { startDate: term.startDate, endDate: term.endDate })} />
					<SpeedDialAction icon={<Typography sx={{ p: 2 }}>New ReportCard</Typography>} onClick={() => dialogs.open('term', {})} />
				</SpeedDial>
			</Paper>
		</Box>
	);
}